import { type CSSProperties } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
} from '@sara/ui/components/sidebar';
import { useTheme } from '@sara/ui/hooks/use-theme';
import { ArrowLeft, Monitor, Moon, RotateCcw, Sun } from 'lucide-react';
import { MeDropdown } from '../../contexts/auth';

/**
 * Chrome for the staff-only `/admin/*` subtree. Deliberately separate from
 * `AppLayout` (which is hard org-scoped — `useOrgSlug()` throws without an
 * active org): a super-admin may not belong to any org, so admin pages get
 * their own minimal shell — a "Back to App" link + admin nav + account/theme —
 * and, crucially, a scroll container so long pages scroll.
 */
export function AdminLayout() {
  return (
    <SidebarProvider
      defaultOpen
      className="h-full"
      style={{ '--sidebar-width': '16rem' } as CSSProperties}
    >
      <AdminShell />
    </SidebarProvider>
  );
}

function AdminShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isBulkReplayActive = pathname.startsWith('/admin/bulk-replay');

  return (
    <>
      <Sidebar collapsible="icon" variant="floating" side="left">
        <SidebarHeader className="px-2 py-1.5 group-data-[collapsible=icon]:hidden">
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Staff admin
          </span>
        </SidebarHeader>

        <SidebarSeparator className="bg-foreground/10 group-data-[collapsible=icon]:hidden" />

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Back to App">
                    <Link to="/orgs">
                      <ArrowLeft strokeWidth={1.6} aria-hidden />
                      <span>Back to App</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Bulk Replay" isActive={isBulkReplayActive}>
                    <Link to="/admin/bulk-replay">
                      <RotateCcw strokeWidth={1.6} aria-hidden />
                      <span>Bulk Replay</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="gap-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <ThemeToggleMenuItem />
            </SidebarMenuItem>
          </SidebarMenu>
          <SidebarSeparator className="bg-foreground/10 group-data-[collapsible=icon]:hidden" />
          <SidebarMenu>
            <SidebarMenuItem>
              <MeDropdown />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="min-h-0">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <header className="sticky top-0 z-10 bg-transparent">
            <div className="flex h-14 w-full items-center gap-3 px-3">
              <SidebarTrigger className="shrink-0 text-muted-foreground hover:text-foreground" />
            </div>
          </header>

          <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col px-4 pb-12 pt-4 md:px-10 md:pt-6">
            <Outlet />
          </div>
        </div>
      </SidebarInset>
    </>
  );
}

function ThemeToggleMenuItem() {
  const { theme, setTheme } = useTheme();
  const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
  const label = theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System';
  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;

  return (
    <SidebarMenuButton
      type="button"
      tooltip={`Theme: ${label}`}
      aria-label={`Theme: ${label}. Click to switch.`}
      onClick={() => setTheme(next)}
    >
      <Icon strokeWidth={1.6} aria-hidden />
      <span>Theme: {label}</span>
    </SidebarMenuButton>
  );
}
